/**
 * 9 yaşına uygun ortak UI bileşenleri (Spec Bölüm 4.5).
 *
 * - min 64px dokunma alanı
 * - büyük, kalın font
 * - emoji + renk + metin üçlüsü
 * - WCAG AA kontrast
 * - aria-label, focus ring
 *
 * BigButton tasarım sistemindeki `.btn` sınıfını kullanır — tüm sayfalarda
 * aynı görünür. Dışarıdan `style` ile küçük override'lara izin verilir.
 */

import { ButtonHTMLAttributes, ReactNode } from 'react';

// ============================================================================
// BigButton — Ana aksiyonlar için (min 64px)
// ============================================================================

type Variant = 'primary' | 'accent' | 'success' | 'ghost' | 'danger';

interface BigButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  icon?: string; // emoji
  fullWidth?: boolean;
  loading?: boolean;
}

const VARIANT_CLASS: Record<Variant, string> = {
  primary: 'btn btn--primary',
  accent: 'btn btn--accent',
  success: 'btn btn--success',
  ghost: 'btn btn--ghost',
  danger: 'btn btn--danger',
};

export function BigButton({
  children,
  variant = 'primary',
  icon,
  fullWidth,
  loading,
  disabled,
  className,
  style,
  ...rest
}: BigButtonProps) {
  const classes = [
    VARIANT_CLASS[variant],
    'btn--lg', // 64px touch target by default
    fullWidth ? 'btn--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={classes}
      style={style}
    >
      {loading ? (
        <span aria-label="Yükleniyor">⏳</span>
      ) : icon ? (
        <span aria-hidden="true">{icon}</span>
      ) : null}
      {children}
    </button>
  );
}

// ============================================================================
// ScoreBadge — Puan bant rozeti (Yetersiz/Yüzeysel/İyi/Güçlü)
// ============================================================================

interface ScoreBadgeProps {
  band: 'yetersiz' | 'yuzeysel' | 'iyi' | 'guclu' | '';
  score: number | null;
}

const BAND_META: Record<string, { label: string; icon: string; className: string }> = {
  yetersiz: { label: 'Yetersiz', icon: '😕', className: 'score-band--yetersiz' },
  yuzeysel: { label: 'Yüzeysel', icon: '😐', className: 'score-band--yuzeysel' },
  iyi:      { label: 'İyi',      icon: '👍', className: 'score-band--iyi' },
  guclu:    { label: 'Güçlü',    icon: '🏆', className: 'score-band--guclu' },
};

export function ScoreBadge({ band, score }: ScoreBadgeProps) {
  if (!band || score === null) return null;
  const meta = BAND_META[band];
  if (!meta) return null;
  return (
    <span
      role="status"
      aria-label={`${meta.label} bant, ${score} puan`}
      className={`score-band ${meta.className}`}
    >
      <span aria-hidden="true">{meta.icon}</span>
      <span>{meta.label}</span>
      <span className="score-band__sep" aria-hidden="true">·</span>
      <span>{score}</span>
    </span>
  );
}

// ============================================================================
// EvidenceTypePicker — Kanıt türü seçici (Spec Bölüm 7.4)
// ============================================================================

export const EVIDENCE_TYPES = [
  { value: 'anket',  label: 'Anket / Veri',  icon: '📊' },
  { value: 'foto',   label: 'Fotoğraf',      icon: '📸' },
  { value: 'ses',    label: 'Ses Kaydı',     icon: '🎤' },
  { value: 'video',  label: 'Video',         icon: '🎥' },
  { value: 'cizim',  label: 'Çizim / Şema',  icon: '✏️' },
  { value: 'metin',  label: 'Metin / Not',   icon: '📝' },
] as const;

export type EvidenceType = typeof EVIDENCE_TYPES[number]['value'];

interface EvidenceTypePickerProps {
  value: EvidenceType;
  onChange: (v: EvidenceType) => void;
}

export function EvidenceTypePicker({ value, onChange }: EvidenceTypePickerProps) {
  return (
    <fieldset className="evidence-picker">
      <legend className="evidence-picker__legend">Kanıt türünü seç:</legend>
      <div className="evidence-picker__grid">
        {EVIDENCE_TYPES.map((t) => {
          const selected = value === t.value;
          return (
            <label
              key={t.value}
              className={`evidence-picker__option${selected ? ' is-selected' : ''}`}
            >
              <input
                type="radio"
                name="evidence-type"
                value={t.value}
                checked={selected}
                onChange={() => onChange(t.value)}
                className="visually-hidden"
                aria-label={t.label}
              />
              <span className="evidence-picker__icon" aria-hidden="true">
                {t.icon}
              </span>
              <span className="evidence-picker__label">{t.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

// ============================================================================
// EmptyState — Veri yoksa cesaretlendirici mesaj (cezalandırma yok)
// ============================================================================

interface EmptyStateProps {
  icon?: string;
  title: string;
  message?: string;
  action?: ReactNode;
}

export function EmptyState({ icon = '🌱', title, message, action }: EmptyStateProps) {
  return (
    <div className="empty-state empty-state--soft">
      <div className="empty-state__icon" aria-hidden="true">
        {icon}
      </div>
      <h3 className="empty-state__title">{title}</h3>
      {message && <p className="empty-state__message">{message}</p>}
      {action && <div className="empty-state__action">{action}</div>}
    </div>
  );
}

// ============================================================================
// LoadingDots — Hafif, çocuk dostu yükleniyor göstergesi
// ============================================================================

export function LoadingDots({ message = 'Yükleniyor' }: { message?: string }) {
  return (
    <div className="loading-dots" role="status" aria-live="polite">
      <span className="loading-dots__icon" aria-hidden="true">⏳</span>
      <span>{message}…</span>
    </div>
  );
}